import { HttpClient, httpResource } from '@angular/common/http';
import type { Signal } from '@angular/core';
import { inject, Injectable } from '@angular/core';

import type { AssetCountByConditionAndMaterialResponse } from '../../domain/assets/asset-visualization.model';
import type {
  SidewalkAssetResponse,
  UpdateSidewalkAssetRequest,
} from '../../domain/assets/sidewalk-asset.model';
import { assertIsDefined } from '../../domain/types';
import type { ChartData } from '../../domain/ui/chart-data.model';
import type { GaugeData } from '../../domain/ui/radial-gauge.model';
import { DataInvalidationService } from '../data-invalidation.service';
import { TenantContext } from '../tenant.context';

@Injectable({
  providedIn: 'root',
})
export class SidewalkAssetService {
  readonly #httpClient = inject(HttpClient);
  readonly #tenantContext = inject(TenantContext);
  readonly #dataInvalidationService = inject(DataInvalidationService);

  sidewalkAssetResource(sidewalkAssetId: Signal<string>) {
    return httpResource<SidewalkAssetResponse>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const sidewalkAssetIdValue = sidewalkAssetId();
      return tenantId && sidewalkAssetIdValue
        ? `api/tenants/${tenantId}/assets/sidewalks/${sidewalkAssetIdValue}`
        : undefined;
    });
  }

  updateSidewalkAsset(assetId: string, request: UpdateSidewalkAssetRequest) {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);
    return this.#httpClient.put<SidewalkAssetResponse>(
      `api/tenants/${tenantId}/assets/sidewalks/${assetId}`,
      request,
    );
  }

  sidewalkAssetAverageConditionScoreResource() {
    return httpResource<GaugeData>(() => {
      this.#dataInvalidationService.track('assets');
      const tenantId = this.#tenantContext.currentTenantId();
      return tenantId
        ? `api/tenants/${tenantId}/assets/sidewalks/visualization/average-condition-score`
        : undefined;
    });
  }

  sidewalkAssetSurfaceAreaByConditionResource() {
    return httpResource<ChartData[]>(() => {
      this.#dataInvalidationService.track('assets');
      const tenantId = this.#tenantContext.currentTenantId();
      return tenantId
        ? `api/tenants/${tenantId}/assets/sidewalks/visualization/surface-area-by-condition`
        : undefined;
    });
  }

  sidewalkAssetCenterlineMilesByMaterialResource() {
    return httpResource<ChartData[]>(() => {
      this.#dataInvalidationService.track('assets');
      const tenantId = this.#tenantContext.currentTenantId();
      return tenantId
        ? `api/tenants/${tenantId}/assets/sidewalks/visualization/centerline-miles-by-material`
        : undefined;
    });
  }

  sidewalkAssetCountByConditionAndMaterialResource() {
    return httpResource<AssetCountByConditionAndMaterialResponse[]>(() => {
      this.#dataInvalidationService.track('assets');
      const tenantId = this.#tenantContext.currentTenantId();
      return tenantId
        ? `api/tenants/${tenantId}/assets/sidewalks/visualization/asset-count-by-condition-and-material`
        : undefined;
    });
  }
}
